import { motion } from 'motion/react';
import { Card } from './ui/card';
import { Cloud, CloudRain, Sun, Wind } from 'lucide-react';

interface ForecastHour {
  time: string;
  temp: number;
  condition: 'sunny' | 'cloudy' | 'rainy' | 'windy';
  windSpeed: number;
  humidity: number;
}

interface HourlyForecastProps {
  forecast: ForecastHour[];
}

const conditionIcons = {
  sunny: Sun,
  cloudy: Cloud,
  rainy: CloudRain,
  windy: Wind,
};

const conditionColors = {
  sunny: 'text-yellow-500',
  cloudy: 'text-gray-500',
  rainy: 'text-blue-500',
  windy: 'text-teal-500',
};

export function HourlyForecast({ forecast }: HourlyForecastProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.6 }}
    >
      <Card className="p-6">
        <h3 className="mb-6">Prognoza na najbliższe godziny</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          {forecast.map((hour, index) => {
            const Icon = conditionIcons[hour.condition];
            const iconColor = conditionColors[hour.condition];
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.7 + index * 0.05, duration: 0.3 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="flex flex-col items-center gap-3 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm text-gray-600">{hour.time}</span>
                
                <motion.div
                  animate={{ 
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    repeatDelay: 2,
                    delay: index * 0.2
                  }}
                >
                  <Icon className={`w-8 h-8 ${iconColor}`} />
                </motion.div>
                
                <span className="text-xl">{hour.temp.toFixed(2)}°</span>
                
                <div className="flex flex-col items-center gap-1 text-xs text-gray-500">
                  <span>{hour.windSpeed.toFixed(2)} km/h</span>
                  <span>{hour.humidity.toFixed(2)}%</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Card>
    </motion.div>
  );
}
